import {
    BOOKING_PORTAL_VARIANTS,
    type BookingPortalVariant,
} from "./bookingPortal";

export type BookingPortalServicePresentation =
    | "choice-cards"
    | "compact-select"
    | "professional-service-list"
    | "persistent-service-rail"
    | "service-filter-chips";

export type BookingPortalAvailabilityPresentation =
    | "month-with-day-slots"
    | "resource-time-matrix"
    | "professional-month-slots"
    | "weekly-venue-timetable"
    | "chronological-slot-feed";

export type BookingPortalResourcePresentation =
    | "dedicated-choice-step"
    | "availability-lanes"
    | "professional-cards-first"
    | "single-resource-filter"
    | "resolved-from-slot";

export type BookingPortalMobilePresentation =
    | "stacked-step-flow"
    | "scrollable-availability-board"
    | "team-led-step-flow"
    | "service-tabs-over-day-agenda"
    | "stacked-nearest-appointments";

/**
 * Presentation-only metadata used by variant pickers and previews. Behaviour,
 * steps, initial selection and checkout mode stay owned by
 * BOOKING_PORTAL_VARIANT_CONTRACTS.
 */
export type BookingPortalVariantPresentation = {
    service: BookingPortalServicePresentation;
    resource: BookingPortalResourcePresentation;
    availability: BookingPortalAvailabilityPresentation;
    mobile: BookingPortalMobilePresentation;
    previewTranslationKey: BookingPortalVariant;
};

export const BOOKING_PORTAL_VARIANT_PRESENTATIONS: Readonly<
    Record<BookingPortalVariant, BookingPortalVariantPresentation>
> = {
    guided: {
        service: "choice-cards",
        resource: "dedicated-choice-step",
        availability: "month-with-day-slots",
        mobile: "stacked-step-flow",
        previewTranslationKey: "guided",
    },
    schedule: {
        service: "compact-select",
        resource: "availability-lanes",
        availability: "resource-time-matrix",
        mobile: "scrollable-availability-board",
        previewTranslationKey: "schedule",
    },
    professional: {
        service: "professional-service-list",
        resource: "professional-cards-first",
        availability: "professional-month-slots",
        mobile: "team-led-step-flow",
        previewTranslationKey: "professional",
    },
    "venue-grid": {
        service: "persistent-service-rail",
        resource: "single-resource-filter",
        availability: "weekly-venue-timetable",
        mobile: "service-tabs-over-day-agenda",
        previewTranslationKey: "venue-grid",
    },
    "quick-slots": {
        service: "service-filter-chips",
        resource: "resolved-from-slot",
        availability: "chronological-slot-feed",
        mobile: "stacked-nearest-appointments",
        previewTranslationKey: "quick-slots",
    },
};

export function bookingPortalVariantPresentation(
    variant: BookingPortalVariant,
): BookingPortalVariantPresentation {
    return BOOKING_PORTAL_VARIANT_PRESENTATIONS[variant];
}

export function bookingPortalVariantPresentationEntries(): ReadonlyArray<
    readonly [BookingPortalVariant, BookingPortalVariantPresentation]
> {
    return BOOKING_PORTAL_VARIANTS.map((variant) => [
        variant,
        BOOKING_PORTAL_VARIANT_PRESENTATIONS[variant],
    ] as const);
}
